// @ts-strict-ignore
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    AppPlatform,
    AppPreviewSet,
    AppStoreType,
    LocalizationStatus,
    LocalizedMetadata,
    Release,
    ReleaseStepType,
    RunwayModelsResponse,
    RunwayResponse,
    RunwayResponseType,
    RunwayResponseV2,
    ScreenshotLocalization,
} from '../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { antGold, antGrey, runwayColors } from '@/components/styles/globalStyles';
import Spin from 'antd/lib/spin';
import {
    CloseOutlined,
    InfoCircleOutlined,
    PictureOutlined,
    PlayCircleFilled,
    PlayCircleOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import Dragger from 'antd/lib/upload/Dragger';
import { withAuthorizationHeaders } from '@/api';
import { RootState } from '@/reducers/reducers';
import { selectAuthCredentials, selectIsWorkflowsCIStepEnabled, selectStepRun } from '@/selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { globalMessage } from '@/utils/globalMessage';
import Image from 'antd/lib/image';
import ReactPlayer from 'react-player/file';
import { getIsApplePlatform } from '@/helpers';
import Button from 'antd/lib/button';
import { ReleaseUpdatable } from '@/actions/updatable';
import { releaseEndpoint, stepRunEndpoint } from '@/helpers/apiHelpers';
import {
    DeleteAppPreviewRequest,
    DeleteScreenshotRequestReleaseStep,
    tryUpdateRelease,
    UpdateReleaseResponse,
    updateReleaseSuccess,
} from '@/actions/release';
import Alert from 'antd/lib/alert';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import SmallStatusNode from '@/components/SmallStatusNode';
import { displayTextForReleaseStepType, statusColorForLocalizationStatus } from '@/helpers/displayHelpers';
import Divider from 'antd/lib/divider';
import YouTubePlayer from 'react-player/youtube';
import Tooltip from 'antd/lib/tooltip';
import { useCallUpdatableForCollections, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { DeleteScreenshotRequest, StepRunUpdatable, updateStepRuns } from '@/actions/stepRun';
import { StepRun } from '@/types/stepRun';

interface LocalizationContentProps extends ReleaseComponentProps {
    localization: ScreenshotLocalization & {
        appPreviewSets: AppPreviewSet[]; // for iOS only
    };
    platform: AppPlatform;
    disableEditing: boolean;
    androidVideoUrlLocalizations: LocalizedMetadata[];
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const MAXIMUM_APP_PREVIEW_COUNT = 3;
const MAXIMUM_SCREENSHOT_COUNT = 10;

const HEIGHT = '22vw';
const MAX_HEIGHT = `480px`;
const MIN_HEIGHT = `400px`;
const BORDER_RADIUS = `calc(max(min(${HEIGHT} / 14, ${MAX_HEIGHT} / 14), ${MIN_HEIGHT} / 14))`;

const errorToHumanReadableError = (error: string) => {
    switch (error) {
        case 'IMAGE_INCORRECT_DIMENSIONS':
            return 'The dimensions of this screenshot are wrong.';
        case 'MOV_RESAVE_STEREO':
            return 'This video contains unsupported or corrupted audio.';
        case 'MOV_RESAVE_FRAME_RATE_SMALLER':
            return 'The frame rate of this video is too high.';
        default:
            return error;
    }
};

interface ScreenshotOrVideoContainerProps {
    id: string | null;
    onDelete: (id: string) => void;
    imageURL: string; // could be screenshot or video thumbnail
    videoURL?: string;
    error?: string;
    isDisabled: boolean;
    androidVideoUrl?: string;
    isDeletingScreenshotForStepRun: boolean;
}

const ScreenshotOrVideoContainer: React.FC<ScreenshotOrVideoContainerProps> = ({
    onDelete,
    imageURL,
    videoURL,
    error,
    id,
    isDisabled,
    androidVideoUrl,
    isDeletingScreenshotForStepRun,
}) => {
    const isVideo = !!videoURL || videoURL === '' || !!androidVideoUrl;

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    const handleImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.target;
        setDimensions({ width: naturalWidth, height: naturalHeight });
    };

    const isProcessingVideo = !imageURL && videoURL === '' && !error;

    const deletionErrorReleaseStep = useError(ReleaseUpdatable.DeleteScreenshot, id);
    const isDeletingReleaseStep = useIsLoading(
        isVideo ? ReleaseUpdatable.DeleteAppPreview : ReleaseUpdatable.DeleteScreenshot,
        id,
    );

    const deletionAppPreviewError = useError(ReleaseUpdatable.DeleteAppPreview, id);
    const deleteAppPreviewSuccess = useUpdated(ReleaseUpdatable.DeleteAppPreview, id);
    const androidVideoId = androidVideoUrl ? new URL(androidVideoUrl).searchParams.get('v') : null;

    useEffect(() => {
        if (deletionErrorReleaseStep) {
            globalMessage.error(deletionErrorReleaseStep);
        }
    }, [deletionErrorReleaseStep]);

    useEffect(() => {
        if (deletionAppPreviewError) {
            globalMessage.error(deletionAppPreviewError);
        }
    }, [deletionAppPreviewError]);

    useEffect(() => {
        if (deleteAppPreviewSuccess) {
            globalMessage.success('App preview deleted successfully');
        }
    }, [deleteAppPreviewSuccess]);

    const hasImage = imageURL && !(isProcessingVideo || error);
    const isDeleting = isDeletingScreenshotForStepRun ?? isDeletingReleaseStep;

    return (
        <div
            style={{
                width: 'auto',
                flexShrink: 0,
                height: HEIGHT,
                maxHeight: MAX_HEIGHT,
                minHeight: MIN_HEIGHT,
                ...(!hasImage && {
                    aspectRatio: '9 / 16',
                }),
            }}
            className="screenshot-delete-container"
        >
            {isDeleting ?
                <div
                    style={{
                        backgroundColor: 'rgba(1, 1, 1, 0.5)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: BORDER_RADIUS,
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        top: '0px',
                        left: '0px',

                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                    }}
                >
                    <Spin size="large" />
                </div>
            :   !!id && (
                    <>
                        <Button
                            shape="circle"
                            size="large"
                            style={{
                                position: 'absolute',
                                top: '-15px',
                                right: '-15px',
                                transition: 'opacity 0.3s',
                                backgroundColor: runwayColors.statusRed,
                                border: `2px solid ${runwayColors.white}`,
                                zIndex: 2,
                            }}
                            onClick={() => {
                                onDelete(id);
                            }}
                            disabled={isDeleting || isDisabled}
                        >
                            <CloseOutlined
                                style={{
                                    color: `${runwayColors.white}`,
                                }}
                            />
                        </Button>
                    </>
                )
            }

            <div
                style={{
                    // width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: BORDER_RADIUS,
                    border: `4px solid ${error ? runwayColors.statusRed : antGrey.grey4}`,
                    position: 'relative',
                    aspectRatio: `${dimensions.width} / ${dimensions.height}`,
                }}
            >
                {isVideo && !error && !isProcessingVideo && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1,
                            backgroundColor: 'var(--rw-overlay-dark)',
                            pointerEvents: 'none',
                        }}
                    >
                        <PlayCircleFilled
                            className="screenshot-overlay"
                            style={{ fontSize: 52, color: runwayColors.white, transition: 'opacity 0.3s' }}
                        />
                    </div>
                )}
                {androidVideoUrl ?
                    <Image
                        src={`https://img.youtube.com/vi/${androidVideoId}/hqdefault.jpg`}
                        width="auto"
                        height="100%"
                        preview={{
                            toolbarRender: () => {
                                return null;
                            },
                            imageRender: () => {
                                return <YouTubePlayer url={androidVideoUrl} controls />;
                            },
                        }}
                    />
                :   <>
                        {!hasImage && (
                            <div
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                    padding: 24,
                                }}
                            >
                                {isProcessingVideo && (
                                    <Alert
                                        closable={false}
                                        message={`Video is being processed...`}
                                        type="success"
                                        style={{
                                            textAlign: 'center',
                                        }}
                                    />
                                )}
                                {error && (
                                    <Alert
                                        closable={false}
                                        message={errorToHumanReadableError(error)}
                                        type="error"
                                        style={{
                                            textAlign: 'center',
                                        }}
                                    />
                                )}
                            </div>
                        )}

                        {hasImage && (
                            <Image
                                src={imageURL}
                                height="100%"
                                width="auto"
                                onLoad={handleImageLoad}
                                preview={{
                                    toolbarRender:
                                        isVideo ?
                                            () => {
                                                return null;
                                            }
                                        :   undefined,
                                    imageRender:
                                        isVideo ?
                                            () => {
                                                return (
                                                    <ReactPlayer
                                                        url={videoURL}
                                                        height={'80%'}
                                                        width={'auto'}
                                                        loop
                                                        controls
                                                        style={{
                                                            zIndex: 1,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            backgroundColor: 'transparent',
                                                        }}
                                                    />
                                                );
                                            }
                                        :   undefined,
                                }}
                            />
                        )}
                    </>
                }
            </div>
        </div>
    );
};

interface ScreenshotOrVideoDropzoneProps {
    isDisabled: boolean;
    isApplePlatform: boolean;
    screenshotUploadURL: string;
    appPreviewUploadURL: string;
    data: (isVideo: boolean) => Record<string, unknown>;
    currentScreenshotCount: number;
    currentAppPreviewCount: number;
    stepRun: Nullable<StepRun>;
}

const ScreenshotOrVideoDropzone: React.FC<ScreenshotOrVideoDropzoneProps> = ({
    isDisabled,
    isApplePlatform,
    screenshotUploadURL,
    appPreviewUploadURL,
    data,
    currentScreenshotCount,
    currentAppPreviewCount,
    stepRun,
}) => {
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const [dropError, setDropError] = React.useState<string | null>(null);

    const [isVideo, setIsVideo] = React.useState(false);
    const [isDraggingOver, setIsDraggingOver] = React.useState(false);

    const [isUploading, setIsUploading] = React.useState(false);

    const isAtMax =
        isVideo ?
            currentAppPreviewCount >= MAXIMUM_APP_PREVIEW_COUNT
        :   currentScreenshotCount >= MAXIMUM_SCREENSHOT_COUNT;

    const checkEvent = (event) => {
        if (event.dataTransfer.items.length === 1) {
            const type = event.dataTransfer.items[0].type;
            // check if files are .mov, .m4v, .mp4 for apple platforms
            if (isApplePlatform && (type === 'video/quicktime' || type === 'video/mp4')) {
                setIsVideo(true);
                if (currentAppPreviewCount >= MAXIMUM_APP_PREVIEW_COUNT) {
                    setDropError('Maximum app preview count reached');
                } else {
                    setDropError(null);
                }
                // check if files are .png or .jpeg
            } else if (type === 'image/png' || type === 'image/jpeg' || type === 'image/jpg') {
                if (currentScreenshotCount >= MAXIMUM_SCREENSHOT_COUNT) {
                    setDropError('Maximum screenshot count reached');
                }
                setIsVideo(false);
            } else {
                setDropError('Unsupported file type');
            }
        } else {
            setDropError('Drop only one file at a time');
        }
        setIsDraggingOver(true);
    };

    const dispatch = useDispatch();

    return (
        <div
            style={{
                width: 'auto',
                height: HEIGHT,
                maxHeight: MAX_HEIGHT,
                minHeight: MIN_HEIGHT,
                aspectRatio: '9 / 16',
            }}
        >
            <Dragger
                disabled={isUploading || isDisabled || isAtMax}
                multiple={false}
                data={(file) => {
                    const isVideo = file.type.includes('video');
                    return data(isVideo);
                }}
                onChange={(uploadParams) => {
                    const isUploading = uploadParams.file.status === 'uploading';
                    setIsUploading(isUploading);

                    if (uploadParams.file.status === 'error') {
                        globalMessage.error('Error uploading file. Please try again');
                        return;
                    }

                    if (uploadParams.file.status === 'done') {
                        const shouldUseWorkflowsEndpoints = !!stepRun;
                        const response = uploadParams.file.response as RunwayResponse | RunwayResponseV2;
                        const success =
                            shouldUseWorkflowsEndpoints ?
                                (response as RunwayResponseV2).type === RunwayResponseType.Success
                            :   (response as RunwayResponse).success;

                        if (!success) {
                            const errorMessage =
                                shouldUseWorkflowsEndpoints ?
                                    (response as RunwayResponseV2).message
                                :   (response as RunwayResponse).errorMessage;

                            globalMessage.error(`Error uploading file: ${errorMessage}`);
                            return;
                        }

                        globalMessage.success(isVideo ? 'App preview uploaded!' : 'Screenshot uploaded!');

                        if (shouldUseWorkflowsEndpoints) {
                            const data = response.data as RunwayModelsResponse;
                            dispatch(updateStepRuns(data.stepRuns));
                            return;
                        }

                        const release = response.data as Release;
                        const updatedReleaseResponse: UpdateReleaseResponse = {
                            releaseId: release.id,
                            release,
                        };

                        dispatch(updateReleaseSuccess(updatedReleaseResponse));
                        return;
                    }
                }}
                action={(file) => {
                    const isVideo = file.type.includes('video');
                    const isAtMax =
                        isVideo ?
                            currentAppPreviewCount >= MAXIMUM_APP_PREVIEW_COUNT
                        :   currentScreenshotCount >= MAXIMUM_SCREENSHOT_COUNT;

                    if (isAtMax || dropError) {
                        return undefined;
                    }

                    return isVideo ? appPreviewUploadURL : screenshotUploadURL;
                }}
                headers={withAuthorizationHeaders(authCredentials, {})}
                method="POST"
                accept={`.mov,.m4v,.mp4,image/png,image/jpeg,image/jpg`}
                showUploadList={false}
                onDrop={(e) => {
                    checkEvent(e);
                    setIsDraggingOver(false);
                    setDropError(null);
                }}
                style={{
                    backgroundColor: `${runwayColors.white}`,
                    margin: 'auto',
                    border: `4px dashed ${antGrey.grey4}`,
                    borderColor: antGrey.grey4,
                    width: 'auto',
                    height: HEIGHT,
                    maxHeight: MAX_HEIGHT,
                    minHeight: MIN_HEIGHT,
                    borderRadius: BORDER_RADIUS,
                    aspectRatio: '9 / 16',
                    ...(isDraggingOver &&
                        (dropError ?
                            {
                                borderColor: runwayColors.statusRed,
                            }
                        :   {
                                borderColor: runwayColors.statusGreen,
                            })),
                }}
            >
                <div
                    onDragOver={checkEvent}
                    onDragLeave={() => {
                        setIsDraggingOver(false);
                    }}
                    onDragExit={() => {
                        setIsDraggingOver(false);
                    }}
                    style={{
                        backgroundColor: runwayColors.white,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        gap: 12,
                        padding: 24,
                    }}
                >
                    {isUploading ?
                        <Spin size="large" />
                    :   <>
                            {isDraggingOver &&
                                !dropError &&
                                (isVideo ?
                                    <VideoCameraOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 30,
                                            pointerEvents: 'none',
                                        }}
                                    />
                                :   <PictureOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 30,
                                            pointerEvents: 'none',
                                        }}
                                    />)}

                            <div
                                style={{
                                    pointerEvents: 'none',
                                    fontSize: 20,
                                    lineHeight: `28px`,
                                    color: dropError ? runwayColors.statusRed : antGrey.grey6,
                                    fontWeight: 600,
                                    textAlign: 'center',
                                }}
                            >
                                {isDraggingOver ?
                                    dropError ?
                                        dropError
                                    :   `Drop to upload ${isVideo ? 'app preview' : 'screenshot'}...`
                                :   <>
                                        Click or drag to upload{' '}
                                        {isApplePlatform ? 'app previews or screenshots' : 'screenshots'}
                                    </>
                                }
                            </div>
                        </>
                    }
                </div>
            </Dragger>
        </div>
    );
};

const ScreenshotOrVideoCount: React.FC<{
    count: number;
    isVideo?: boolean;
    isAndroid?: boolean;
    isWorkflowsCIStepEnabled: boolean;
}> = ({ count, isVideo, isAndroid, isWorkflowsCIStepEnabled }) => {
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
            }}
        >
            <Space>
                {isVideo ?
                    <PlayCircleOutlined
                        style={{
                            color: antGrey.grey6,
                        }}
                    />
                :   <PictureOutlined
                        style={{
                            color: antGrey.grey6,
                        }}
                    />
                }

                <Text
                    style={{
                        color: antGrey.grey7,
                    }}
                >
                    {isVideo ?
                        isAndroid ?
                            'Video'
                        :   'App previews'
                    :   'Screenshots'}
                </Text>
                {isVideo && isAndroid && (
                    <Tooltip
                        title={`Video can be edited in the ${displayTextForReleaseStepType(
                            ReleaseStepType.Metadata,
                            AppPlatform.Android,
                            AppStoreType.Google,
                            isWorkflowsCIStepEnabled,
                        )} step.`}
                    >
                        <InfoCircleOutlined style={{ color: runwayColors.info }} />
                    </Tooltip>
                )}
            </Space>
            <Space>
                <Text
                    style={{
                        color: antGrey.grey7,
                    }}
                >
                    {count} of{' '}
                    {isVideo ?
                        isAndroid ?
                            1
                        :   MAXIMUM_APP_PREVIEW_COUNT
                    :   MAXIMUM_SCREENSHOT_COUNT}
                </Text>
            </Space>
        </div>
    );
};

export const StepContentVideoAndScreenshotsLocalizedFrom: React.FC<LocalizationContentProps> = ({
    platform,
    localization,
    disableEditing,
    orgId,
    appId,
    releaseId,
    androidVideoUrlLocalizations,
    stepRunComponentProps,
}) => {
    const isApplePlatform = getIsApplePlatform(platform);

    const [selectedDevice, setSelectedDevice] = React.useState(defaultDisplayDeviceForPlatform(platform));

    const appPreviewSet = localization.appPreviewSets.find(
        (appPreviewSet) => appPreviewSet.previewType === appPreviewDeviceForSelectedDevice(selectedDevice),
    );

    const screenshotsForDevice = (device: string) => {
        return localization.screenshotSets.find((screenshotSet) => screenshotSet.screenshotDisplayType === device);
    };

    const fallbackDevice = (() => {
        // https://developer.apple.com/help/app-store-connect/reference/screenshot-specifications/
        const fallbackRuleTo65 = ['APP_IPHONE_58', 'APP_IPHONE_63', 'APP_IPHONE_67'];
        const fallbackRuleTo69 = ['APP_IPHONE_65'];
        const fallbackRuleTo58 = ['APP_IPHONE_55'];
        const fallbackRuleTo55 = ['APP_IPHONE_47'];
        const fallbackRuleTo47 = ['APP_IPHONE_40'];
        const fallbackRuleTo40 = ['APP_IPHONE_35'];

        let usedSize: string | undefined;
        if (fallbackRuleTo65.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_65';
        }
        if (fallbackRuleTo69.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_67';
        }
        if (fallbackRuleTo58.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_58';
        }
        if (fallbackRuleTo55.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_55';
        }
        if (fallbackRuleTo47.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_47';
        }
        if (fallbackRuleTo40.includes(selectedDevice)) {
            usedSize = 'APP_IPHONE_40';
        }

        return usedSize;
    })();

    const screenshotSet = screenshotsForDevice(selectedDevice) ?? screenshotsForDevice(fallbackDevice);

    const dispatch = useDispatch();
    const stepRunId = stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));
    const releaseURL = releaseEndpoint(orgId, appId, releaseId);
    const stepRunURL = stepRun ? stepRunEndpoint(stepRunComponentProps) : null;

    const screenshotUploadURL = React.useMemo(() => {
        const pathURL = stepRunURL ?? releaseURL;
        return `${window.location.origin}/${pathURL}/screenshots/upload`;
    }, [stepRunComponentProps, stepRunURL, releaseURL]);

    const screenshotIds = screenshotSet?.screenshots?.map((screenshot) => screenshot.id) ?? [];
    const stepRunComponentPropsMap = screenshotIds.reduce((acc, id) => ({ ...acc, [id]: stepRunComponentProps }), {});
    const [deleteScreenshotStepRunMap, isDeletingScreenshotMap, responseTypeScreenshotMap, errorScreenshotMap] =
        useCallUpdatableForCollections(StepRunUpdatable.DeleteScreenshot, stepRunComponentPropsMap, screenshotIds);

    const deleteScreenshotAction = (id: string) => {
        if (stepRun) {
            const request: DeleteScreenshotRequest = {
                screenshotId: id,
                language: localization.locale,
                imageType: selectedDevice,
            };

            return deleteScreenshotStepRunMap[id](request);
        }

        const request: DeleteScreenshotRequestReleaseStep = {
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
            screenshotId: id,
            imageType: selectedDevice,
            language: localization.locale,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.DeleteScreenshot, request, id));
    };

    const deleteAppPreviewAction = (id: string) => {
        const request: DeleteAppPreviewRequest = {
            orgId: orgId,
            appId: appId,
            releaseId: releaseId,
            previewId: id,
            imageType: appPreviewDeviceForSelectedDevice(selectedDevice),
            language: localization.locale,
        };

        dispatch(tryUpdateRelease(ReleaseUpdatable.DeleteAppPreview, request, id));
    };

    const isOtherSizeUsed = screenshotSet ? screenshotSet.screenshotDisplayType !== selectedDevice : true;
    const allDisplayTypes = localization.screenshotSets.map((set) => set.screenshotDisplayType);
    const deviceNameMap = deviceNameMapForPlatform(platform, allDisplayTypes);

    const currentAppPreviewCount = appPreviewSet?.appPreviews.length ?? 0;
    const currentScreenshotCount = screenshotSet?.screenshots.length ?? 0;

    const screenshotsForDeviceTypeHaveError = React.useMemo(() => {
        return localization.screenshotSets.reduce((acc, set) => {
            acc[set.screenshotDisplayType] = set.screenshots.some((screenshot) => !!screenshot.uploadError);
            return acc;
        }, {});
    }, [localization]);

    const androidVideoUrl = androidVideoUrlLocalizations.find(
        (localization) => localization.locale === localization.locale,
    )?.videoUrl;

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    useEffect(() => {
        for (const id in errorScreenshotMap) {
            if (errorScreenshotMap[id]) {
                globalMessage.error(errorScreenshotMap[id]);
            }
        }
    }, [errorScreenshotMap]);

    useEffect(() => {
        for (const id in responseTypeScreenshotMap) {
            if (responseTypeScreenshotMap[id] === RunwayResponseType.Success) {
                globalMessage.success('Screenshot deleted successfully');
            }
        }
    }, [responseTypeScreenshotMap]);

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                alignItems: 'stretch',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 36,
                        paddingBottom: 0,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: antGrey.grey3,
                            borderRadius: 8,
                            display: 'flex',
                            width: 240,
                            justifyContent: 'space-between',
                            padding: '8px 10px',
                            flexDirection: 'column',
                        }}
                    >
                        <ScreenshotOrVideoCount
                            count={screenshotSet?.screenshots.length ?? 0}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />
                        <>
                            <Divider
                                style={{
                                    margin: '8px 0',
                                }}
                            />
                            {isApplePlatform ?
                                <ScreenshotOrVideoCount
                                    count={appPreviewSet?.appPreviews.length ?? 0}
                                    isVideo
                                    isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                />
                            :   <ScreenshotOrVideoCount
                                    count={androidVideoUrl ? 1 : 0}
                                    isVideo
                                    isAndroid
                                    isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                />
                            }
                        </>
                    </div>
                    <div>
                        Device:
                        <Select
                            value={selectedDevice}
                            onChange={(value) => setSelectedDevice(value)}
                            style={{ marginLeft: '20px', minWidth: 265 }}
                        >
                            {Object.entries(deviceNameMap).map((device, index) => {
                                const [deviceKey, deviceName] = device;

                                const deviceTypeHasError = screenshotsForDeviceTypeHaveError[deviceKey] === true;

                                return (
                                    <Select.Option value={deviceKey} key={`option-${index}`}>
                                        <Space>
                                            {deviceName}
                                            {deviceTypeHasError && (
                                                <SmallStatusNode
                                                    color={statusColorForLocalizationStatus(LocalizationStatus.Blocked)}
                                                />
                                            )}
                                        </Space>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                    <div
                        style={{
                            width: 240,
                        }}
                    />
                </div>
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {isOtherSizeUsed && !!fallbackDevice && (
                        <Alert
                            closable={false}
                            message={`Using ${deviceNameMap[fallbackDevice]}`}
                            style={{
                                backgroundColor: antGold.gold1,
                                borderColor: antGold.gold4,
                                marginTop: '18px',
                            }}
                        />
                    )}
                </div>
            </div>
            <div
                style={{
                    width: '100%',
                    overflowX: 'auto',
                    padding: '36px 0',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        gap: 24,
                        padding: '0 36px',
                    }}
                >
                    <ScreenshotOrVideoDropzone
                        stepRun={stepRun}
                        currentAppPreviewCount={currentAppPreviewCount}
                        currentScreenshotCount={currentScreenshotCount}
                        isDisabled={disableEditing}
                        isApplePlatform={isApplePlatform}
                        screenshotUploadURL={screenshotUploadURL}
                        appPreviewUploadURL={`${window.location.origin}/${releaseURL}/appPreviews/upload`}
                        data={(isVideo) => {
                            if (isVideo) {
                                const appPreviewSetId = appPreviewSet?.id;
                                if (!appPreviewSetId) {
                                    return {
                                        language: localization.locale,
                                        imageType: appPreviewDeviceForSelectedDevice(selectedDevice),
                                    };
                                } else {
                                    return {
                                        appPreviewSetId,
                                    };
                                }
                            } else {
                                if (platform == AppPlatform.Android || !screenshotSet) {
                                    return {
                                        language: localization.locale,
                                        imageType: selectedDevice,
                                    };
                                } else {
                                    return {
                                        screenshotSetId: screenshotSet.id,
                                    };
                                }
                            }
                        }}
                    />

                    {androidVideoUrl && (
                        <ScreenshotOrVideoContainer
                            isDeletingScreenshotForStepRun={null}
                            id={null}
                            isDisabled={disableEditing}
                            imageURL={androidVideoUrl}
                            onDelete={deleteAppPreviewAction}
                            androidVideoUrl={androidVideoUrl}
                        />
                    )}

                    {appPreviewSet?.appPreviews.map((appPreview) => (
                        <ScreenshotOrVideoContainer
                            isDeletingScreenshotForStepRun={null}
                            isDisabled={disableEditing}
                            key={appPreview.id}
                            id={appPreview.id}
                            imageURL={appPreview.previewImageUrl}
                            onDelete={deleteAppPreviewAction}
                            videoURL={appPreview.url}
                            error={appPreview.uploadError}
                        />
                    ))}
                    {screenshotSet?.screenshots.map((screenshot) => (
                        <ScreenshotOrVideoContainer
                            isDeletingScreenshotForStepRun={stepRun ? isDeletingScreenshotMap[screenshot.id] : null}
                            isDisabled={disableEditing}
                            id={screenshot.id}
                            key={screenshot.id}
                            imageURL={screenshot.url}
                            onDelete={deleteScreenshotAction}
                            error={screenshot.uploadError}
                        />
                    ))}
                    {currentAppPreviewCount > 0 && currentScreenshotCount > 0 && (
                        <div
                            style={{
                                flex: 1,
                                minWidth: 1,
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const defaultDisplayDeviceForPlatform = (platform: AppPlatform) => {
    switch (platform) {
        case AppPlatform.TvOS:
            return 'APP_APPLE_TV';
        case AppPlatform.VisionOS:
            return 'APP_APPLE_VISION_PRO';
        case AppPlatform.Ios:
            return 'APP_IPHONE_67';
        case AppPlatform.Android:
            return 'phoneScreenshots';
        case AppPlatform.WearOS:
            return 'wearScreenshots';
        case AppPlatform.AndroidTV:
            return 'tvScreenshots';
        default:
            return null;
    }
};

const appPreviewDeviceForSelectedDevice = (selectedDevice: string) => {
    // remove the APP_ prefix
    return selectedDevice.replace('APP_', '');
};

const deviceNameMapForPlatform = (platform: AppPlatform, allDisplayTypes: string[]) => {
    switch (platform) {
        case AppPlatform.TvOS:
            return {
                APP_APPLE_TV: 'Apple TV',
            };
        case AppPlatform.VisionOS:
            return {
                APP_APPLE_VISION_PRO: 'Apple Vision Pro',
            };
        case AppPlatform.Ios:
            return {
                APP_IPHONE_35: 'iPhone - 3.5" Display',
                APP_IPHONE_40: 'iPhone - 4" Display',
                APP_IPHONE_47: 'iPhone - 4.7" Display',
                APP_IPHONE_55: 'iPhone - 5.5" Display',
                APP_IPHONE_58: 'iPhone - 5.8" Display',
                APP_IPHONE_63: 'iPhone - 6.3" Display',
                APP_IPHONE_65: 'iPhone - 6.5" Display',
                APP_IPHONE_67: 'iPhone - 6.9" Display',
                ...(allDisplayTypes.includes('APP_IPAD_PRO_3GEN_129') && {
                    APP_IPAD_PRO_3GEN_129: 'iPad Pro (3rd Gen) - 12.9" Display',
                }),
                ...(allDisplayTypes.includes('APP_IPAD_PRO_3GEN_11') && {
                    APP_IPAD_PRO_3GEN_11: 'iPad Pro (3rd Gen) - 11" Display',
                }),
                ...(allDisplayTypes.includes('APP_IPAD_PRO_129') && {
                    APP_IPAD_PRO_129: 'iPad Pro - 12.9" Display',
                }),
                ...(allDisplayTypes.includes('APP_IPAD_105') && {
                    APP_IPAD_105: 'iPad - 10.5" Display',
                }),
                ...(allDisplayTypes.includes('APP_IPAD_97') && {
                    APP_IPAD_97: 'iPad - 9.7" Display',
                }),
            };
        case AppPlatform.WearOS:
            return {
                wearScreenshots: 'Wear screenshots',
            };
        case AppPlatform.AndroidTV:
            return {
                tvScreenshots: 'TV screenshots',
                tvBanner: 'TV banner',
            };
        case AppPlatform.Android:
            return {
                phoneScreenshots: 'Phone screenshots',
                sevenInchScreenshots: '7" screenshots',
                tenInchScreenshots: '10" screenshots',
            };
        default:
            return {};
    }
};
